/**
 * Modal para configurar las preferencias de búsqueda del usuario.
 * Permite modificar filtros demográficos y preferencias de matching.
 * 
 * @param {Object} props - Propiedades del componente
 * @param {boolean} props.isOpen - Estado de apertura del modal
 * @param {function} props.onClose - Función para cerrar el modal
 */

import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import { useCurrentUserProfile } from '../../../hooks/useCurrentUserProfile';
import { useUpdatePreferencesMutation } from '../../../hooks/usePreferencesMutations';
import { useAuth } from '../../../hooks/useAuth';
import { ages, alturas, kilitos, femEtnia, homEtnia, femFisic, homFisic, femRol, homRol, femSituAmorosa, homSituAmorosa } from '../../../constants/humanQualities';
import { supabase } from '../../../lib/supabase';

export const PreferencesModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  
  // Hook para obtener datos del usuario actual
  const { userProfile, isLoading: profileLoading } = useCurrentUserProfile();
  
  // Hook para mutación de preferencias
  const { mutate: updatePreferences, isPending: isUpdating } = useUpdatePreferencesMutation();

  // Estado local del formulario
  const [preferences, setPreferences] = useState({
    sex_pref: 0,
    sex_user: null,
    years_desde: 18,
    years_hasta: 99,
    altura_desde: 122,
    altura_hasta: 241,
    peso_desde: 41,
    peso_hasta: 272,
    etnia: [],
    complexion: [],
    sex_rol: [],
    situ_amorosa: [],
  });

  // Estado para rastrear si hay cambios sin guardar
  const [isFormChanged, setIsFormChanged] = useState(false);

  // Efecto para bloquear el scroll del body cuando el modal está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Efecto para cargar datos cuando se abre el modal
  useEffect(() => {
    if (isOpen && user?.id) {
      // Cargar preferencias desde la tabla preferencias
      const loadPreferences = async () => {
        try {
          const { data, error } = await supabase
            .from('preferencias')
            .select('*')
            .eq('user_ref_id', user.id)
            .maybeSingle();

          if (error && error.code !== 'PGRST116') {
            console.error('[PreferencesModal] Error cargando preferencias:', error);
            return;
          }

          if (data) {
            setPreferences({
              sex_pref: data.sex_pref || 0,
              sex_user: data.sex_user || userProfile?.sex || null,
              years_desde: data.years_desde || 18,
              years_hasta: data.years_hasta || 99,
              altura_desde: data.altura_desde || 122,
              altura_hasta: data.altura_hasta || 241,
              peso_desde: data.peso_desde || 41,
              peso_hasta: data.peso_hasta || 272,
              etnia: data.etnia || [],
              complexion: data.complexion || [],
              sex_rol: data.sex_rol || [],
              situ_amorosa: data.situ_amorosa || [],
            });
          } else {
            // Valores por defecto si no hay preferencias
            setPreferences({
              sex_pref: userProfile?.sex_pref || 0,
              sex_user: userProfile?.sex || null,
              years_desde: 18,
              years_hasta: 99,
              altura_desde: 122,
              altura_hasta: 241,
              peso_desde: 41,
              peso_hasta: 272,
              etnia: [],
              complexion: [],
              sex_rol: [],
              situ_amorosa: [],
            });
          }
          setIsFormChanged(false);
        } catch (err) {
          console.error('[PreferencesModal] Error cargando preferencias:', err);
        }
      };

      loadPreferences();
    }
  }, [isOpen, user?.id, userProfile]);

  // Maneja cambios en los campos del formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setPreferences(prev => {
      const updated = { ...prev };
      
      // Manejar campos numéricos
      const numericFields = ['years_desde', 'years_hasta', 'altura_desde', 'altura_hasta', 'peso_desde', 'peso_hasta', 'sex_pref', 'sex_user'];
      if (numericFields.includes(name)) {
        const newValue = name === 'sex_pref' || name === 'sex_user' ? (value === '' ? null : Number(value)) : Number(value);
        updated[name] = newValue;
        
        // Validar que desde no sea mayor que hasta para rangos
        if (name === 'years_desde' && newValue > prev.years_hasta) {
          updated.years_hasta = newValue;
        } else if (name === 'years_hasta' && newValue < prev.years_desde) {
          updated.years_desde = newValue;
        } else if (name === 'altura_desde' && newValue > prev.altura_hasta) {
          updated.altura_hasta = newValue;
        } else if (name === 'altura_hasta' && newValue < prev.altura_desde) {
          updated.altura_desde = newValue;
        } else if (name === 'peso_desde' && newValue > prev.peso_hasta) {
          updated.peso_hasta = newValue;
        } else if (name === 'peso_hasta' && newValue < prev.peso_desde) {
          updated.peso_desde = newValue;
        }
      } else {
        // Manejar campos de arrays (etnia, complexion, sex_rol, situ_amorosa)
        updated[name] = value === '' ? [] : [Number(value)];
      }
      
      return updated;
    });
    setIsFormChanged(true);
  };

  // Maneja el guardado de preferencias
  const handleSave = async () => {
    if (!isFormChanged) {
      onClose();
      return;
    }

    try {
      const dataToSave = {
        sex_pref: preferences.sex_pref || 0,
        sex_user: preferences.sex_user || userProfile?.sex || null,
        years_desde: preferences.years_desde || 18,
        years_hasta: preferences.years_hasta || 99,
        altura_desde: preferences.altura_desde || 122,
        altura_hasta: preferences.altura_hasta || 241,
        peso_desde: preferences.peso_desde || 41,
        peso_hasta: preferences.peso_hasta || 272,
        etnia: preferences.etnia || [],
        complexion: preferences.complexion || [],
        sex_rol: preferences.sex_rol || [],
        situ_amorosa: preferences.situ_amorosa || [],
      };

      await updatePreferences(dataToSave);
      setIsFormChanged(false);
      onClose();
    } catch (error) {
      console.error('[PreferencesModal] Error guardando preferencias:', error);
    }
  };

  // Maneja el cierre del modal con confirmación si hay cambios
  const handleClose = () => {
    if (isFormChanged) {
      const confirmed = window.confirm('Tienes cambios sin guardar. ¿Deseas cerrar sin guardar?');
      if (!confirmed) return;
    }
    onClose();
  };

  // Si el modal no está abierto, no renderizar nada
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex bg-black/50">
      {/* Backdrop */}
      <div 
        className="flex-1"
        onClick={handleClose}
      />
      
      {/* Drawer */}
      <div className="bg-neutral-800 h-full transition-transform duration-300 ease-in-out w-full md:w-[480px] lg:w-[560px]">
        {/* Header */}
        <div className="h-20 flex items-center justify-between p-4 border-b border-neutral-700">
          <h2 className="text-white text-lg">Preferencias de Búsqueda</h2>
          <button 
            onClick={handleClose}
            className="p-2 hover:bg-neutral-900 rounded-lg transition-colors"
          >
            <FiX size={24} className="text-white" />
          </button>
        </div>

        {/* Formulario */}
        <div className="p-4 space-y-8 overflow-y-auto max-h-[calc(100vh-10rem)]">
          {profileLoading ? (
            <div className="flex items-center justify-center py-8">
              <span className="text-white">Cargando preferencias...</span>
            </div>
          ) : (
            <>
              {/* Preferencia de género */}
              <div>
                <label className="text-white block mb-2">Busco</label>
                <select
                  name="sex_pref"
                  value={preferences.sex_pref}
                  onChange={handleInputChange}
                  className="w-full bg-neutral-900 border-2 border-neutral-700 rounded-lg px-4 py-3 text-white"
                >
                  <option value={0}>Ambos</option>
                  <option value={1}>Mujeres</option>
                  <option value={2}>Hombres</option>
                </select>
              </div>

              {/* Rango de edad */}
              <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-4">
                <label className="text-white block mb-2">Rango de edad: {preferences.years_desde} - {preferences.years_hasta} años</label>
                <div className="space-y-3">
                  <div>
                    <label className="text-gray-400 text-xs mb-1">Desde: {preferences.years_desde} años</label>
                    <input
                      type="range"
                      name="years_desde"
                      value={preferences.years_desde}
                      onChange={handleInputChange}
                      min={ages[0]}
                      max={ages[ages.length - 1]}
                      step={1}
                      className="w-full h-2 bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-rose-700"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs mb-1">Hasta: {preferences.years_hasta} años</label>
                    <input
                      type="range"
                      name="years_hasta"
                      value={preferences.years_hasta}
                      onChange={handleInputChange}
                      min={ages[0]}
                      max={ages[ages.length - 1]}
                      step={1}
                      className="w-full h-2 bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-rose-700"
                    />
                  </div>
                </div>
              </div>

              {/* Rango de altura */}
              <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-4">
                <label className="text-white block mb-2">Rango de altura: {preferences.altura_desde > 0 ? preferences.altura_desde : 'Sin filtro'} - {preferences.altura_hasta > 0 ? preferences.altura_hasta : 'Sin filtro'} cm</label>
                <div className="space-y-3">
                  <div>
                    <label className="text-gray-400 text-xs mb-1">Desde: {preferences.altura_desde > 0 ? preferences.altura_desde + ' cm' : 'Sin filtro'}</label>
                    <input
                      type="range"
                      name="altura_desde"
                      value={preferences.altura_desde}
                      onChange={handleInputChange}
                      min={alturas[0]}
                      max={alturas[alturas.length - 1]}
                      step={1}
                      className="w-full h-2 bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-rose-700"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs mb-1">Hasta: {preferences.altura_hasta > 0 ? preferences.altura_hasta + ' cm' : 'Sin filtro'}</label>
                    <input
                      type="range"
                      name="altura_hasta"
                      value={preferences.altura_hasta}
                      onChange={handleInputChange}
                      min={alturas[0]}
                      max={alturas[alturas.length - 1]}
                      step={1}
                      className="w-full h-2 bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-rose-700"
                    />
                  </div>
                </div>
                <p className="text-gray-400 text-xs mt-1">Deja en el mínimo para no filtrar por altura</p>
              </div>

              {/* Rango de peso */}
              <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-4">
                <label className="text-white block mb-2">Rango de peso: {preferences.peso_desde > 0 ? preferences.peso_desde : 'Sin filtro'} - {preferences.peso_hasta > 0 ? preferences.peso_hasta : 'Sin filtro'} kg</label>
                <div className="space-y-3">
                  <div>
                    <label className="text-gray-400 text-xs mb-1">Desde: {preferences.peso_desde > 0 ? preferences.peso_desde + ' kg' : 'Sin filtro'}</label>
                    <input
                      type="range"
                      name="peso_desde"
                      value={preferences.peso_desde}
                      onChange={handleInputChange}
                      min={kilitos[0]}
                      max={kilitos[kilitos.length - 1]}
                      step={1}
                      className="w-full h-2 bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-rose-700"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs mb-1">Hasta: {preferences.peso_hasta > 0 ? preferences.peso_hasta + ' kg' : 'Sin filtro'}</label>
                    <input
                      type="range"
                      name="peso_hasta"
                      value={preferences.peso_hasta}
                      onChange={handleInputChange}
                      min={kilitos[0]}
                      max={kilitos[kilitos.length - 1]}
                      step={1}
                      className="w-full h-2 bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-rose-700"
                    />
                  </div>
                </div>
                <p className="text-gray-400 text-xs mt-1">Deja en el mínimo para no filtrar por peso</p>
              </div>

              {/* Origen étnico */}
                <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-4">
                  <label className="text-white block mb-2">Origen étnico</label>
                  <select
                    name="etnia"
                    value={preferences.etnia.length > 0 ? preferences.etnia[0] : ''}
                    onChange={handleInputChange}
                    className="w-full bg-neutral-800 border-2 border-neutral-700 rounded-lg px-4 py-3 text-white"
                  >
                    <option value="">Cualquiera</option>
                    {(preferences.sex_pref === 1 ? femEtnia : homEtnia).map((etnia, index) => (
                      <option key={index} value={index}>
                        {etnia}
                      </option>
                    ))}
                  </select>
                </div>

              {/* Complexión física */}
                <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-4">
                  <label className="text-white block mb-2">Complexión física</label>
                  <select
                    name="complexion"
                    value={preferences.complexion.length > 0 ? preferences.complexion[0] : ''}
                    onChange={handleInputChange}
                    className="w-full bg-neutral-800 border-2 border-neutral-700 rounded-lg px-4 py-3 text-white"
                  >
                    <option value="">Cualquiera</option>
                    {(preferences.sex_pref === 1 ? femFisic : homFisic).map((fisic, index) => (
                      <option key={index} value={index}>
                        {fisic}
                      </option>
                    ))}
                  </select>
                </div>

              {/* Rol */}
                <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-4">
                  <label className="text-white block mb-2">Rol</label>
                  <select
                    name="sex_rol"
                    value={preferences.sex_rol.length > 0 ? preferences.sex_rol[0] : ''}
                    onChange={handleInputChange}
                    className="w-full bg-neutral-800 border-2 border-neutral-700 rounded-lg px-4 py-3 text-white"
                  >
                    <option value="">Cualquiera</option>
                    {(preferences.sex_pref === 1 ? femRol : homRol).map((rol, index) => (
                      <option key={index} value={index}>
                        {rol}
                      </option>
                    ))}
                  </select>
                </div>

              {/* Situación amorosa */}
                <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-4">
                  <label className="text-white block mb-2">Situación amorosa</label>
                  <select
                    name="situ_amorosa"
                    value={preferences.situ_amorosa.length > 0 ? preferences.situ_amorosa[0] : ''}
                    onChange={handleInputChange}
                    className="w-full bg-neutral-800 border-2 border-neutral-700 rounded-lg px-4 py-3 text-white"
                  >
                    <option value="">Cualquiera</option>
                    {(preferences.sex_pref === 1 ? femSituAmorosa : homSituAmorosa).map((situacion, index) => (
                      <option key={index} value={index}>
                        {situacion}
                      </option>
                    ))}
                  </select>
                </div>

              {/* Nota informativa */}
              <div className="bg-neutral-700 p-4 rounded-lg">
                <p className="text-gray-300 text-sm">
                  <strong>Nota:</strong> Los filtros se aplicarán automáticamente al radar. 
                  Los valores en el mínimo indican que no hay filtro para ese criterio. Los filtros categóricos 
                  (origen étnico, complexión, rol, situación amorosa) solo aparecen cuando seleccionas un género específico.
                </p>
              </div>
              <div className='h-20'></div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-neutral-700 sticky bottom-0 p-4 bg-neutral-800">
          <button
            onClick={handleSave}
            disabled={!isFormChanged || isUpdating}
            className="w-full py-3 bg-primary rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80 transition-opacity"
            style={{ backgroundColor: '#DA0037' }}
          >
            {isUpdating ? 'Guardando...' : 'Guardar preferencias'}
          </button>
        </div>
      </div>
    </div>
  );
};
